/**
 * @file AppDatabaseMapping.js
 * WHAT: READ-ONLY consolidated compliance report across ALL database technologies. One
 *       row per active + connected database (Oracle / MSSQL / MySQL / PostgreSQL /
 *       Cassandra) showing technology, owning application, environment, deployment, and a
 *       single Compliance column listing only the frameworks the database is actually IN
 *       SCOPE for (applicable AND not excluded by a compliance-scope override).
 * WHY:  With the introduction of the per-technology DB Compliance Matrix, the old
 *       editable Database Mappings screen became redundant — registry authoring (add /
 *       edit / deactivate / in-scope toggling) now lives in each technology's Compliance
 *       Matrix under its Administration menu, gated by that tech's RBAC. This screen is
 *       now a consolidated, view-only report the UAR admin uses to see the whole estate's
 *       effective compliance posture at a glance. All write actions were removed; edits
 *       happen in the matrix. Backed by GET /api/databases/db-compliance-report/.
 */

import React, { useMemo } from 'react';
import axios from 'axios';

import API_CONFIG from '../config/api.config';
import { useSafeAsync, useFilters } from '../hooks';

// Badge colors per database technology (display only).
const DB_TYPE_COLORS = {
  Oracle: 'primary',
  MSSQL: 'warning',
  MySQL: 'success',
  PostgreSQL: 'info',
  Cassandra: 'secondary',
};

// Text-color class per environment (matches the app-wide status-color convention).
const envClass = (env) =>
  env === 'PRODUCTION' ? 'text-danger'
    : env === 'STAGING' ? 'text-warning'
    : env === 'TEST' ? 'text-info'
    : env === 'DEVELOPMENT' ? 'text-success'
    : 'text-muted';

const AppDatabaseMapping = () => {
  // Read-only consolidated report — one row per active+connected DB across all techs,
  // each carrying its resolved IN-SCOPE compliance frameworks.
  const { data: reportData, loading, error, execute: reload } = useSafeAsync(
    async () => {
      const response = await axios.get(API_CONFIG.getDatabasesEndpoint('db-compliance-report/'));
      return response.data?.databases || [];
    },
    [],
    { immediate: true }
  );

  const databases = useMemo(() => reportData || [], [reportData]);

  const { filters, setFilter, clearAllFilters } = useFilters({
    technology: '',
    app_id: '',
    search: '',
    framework: '',
    environment: '',
  });

  // Distinct technologies / environments / frameworks present, for the dropdowns.
  const technologies = useMemo(
    () => [...new Set(databases.map((d) => d.technology_label).filter(Boolean))].sort(),
    [databases]
  );
  const environments = useMemo(
    () => [...new Set(databases.map((d) => d.database_environment).filter(Boolean))].sort(),
    [databases]
  );
  const frameworks = useMemo(() => {
    const set = new Set();
    databases.forEach((d) => (d.in_scope_frameworks || []).forEach((f) => set.add(f)));
    return [...set].sort();
  }, [databases]);

  const filtered = useMemo(() => {
    return databases.filter((d) => {
      if (filters.technology && d.technology_label !== filters.technology) return false;
      if (filters.environment && d.database_environment !== filters.environment) return false;
      if (filters.framework && !(d.in_scope_frameworks || []).includes(filters.framework)) return false;
      if (filters.app_id) {
        // M:N: match against EVERY linked application, not just the primary.
        const needle = filters.app_id.toLowerCase();
        const appHay = (d.applications && d.applications.length
          ? d.applications.map((a) => `${a.app_id || ''} ${a.app_name || ''}`).join(' ')
          : `${d.app_id || ''} ${d.app_name || ''}`).toLowerCase();
        if (!appHay.includes(needle)) return false;
      }
      if (filters.search) {
        const needle = filters.search.toLowerCase();
        const hay = `${d.database_identifier || ''} ${d.display_name || ''} ${d.database_name || ''} ${d.instance || ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [databases, filters]);

  const handleExportCSV = () => {
    if (window.confirm(`Export ${databases.length} database mappings to CSV?`)) {
      window.location.href = API_CONFIG.getDatabasesEndpoint('app-database-mappings/export-csv/');
    }
  };

  if (loading) {
    return (
      <div className="container-fluid mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading database mappings…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="fas fa-database me-2"></i>
                Database Mappings
                <span className="badge bg-secondary ms-2">
                  {filtered.length} of {databases.length}
                </span>
              </h5>
              <div className="d-flex gap-2">
                <button className="btn btn-success btn-sm" onClick={handleExportCSV} disabled={loading}>
                  <i className="fas fa-file-download me-1"></i>Export CSV
                </button>
                <button className="btn btn-outline-primary btn-sm" onClick={reload} disabled={loading}>
                  <i className="fas fa-sync-alt me-1"></i>Refresh
                </button>
              </div>
            </div>

            {/* Read-only notice: editing lives in the per-tech Compliance Matrix. */}
            <div className="card-body pb-0">
              <div style={{
                borderLeft: '4px solid #0d6efd',
                backgroundColor: '#e7f1ff',
                padding: '10px 15px',
                marginBottom: '15px',
                textAlign: 'left',
              }}>
                <i className="fas fa-circle-info me-2 text-primary"></i>
                <strong>Read-only consolidated report.</strong> This shows every active,
                connected database across all technologies and the compliance frameworks
                it is currently <strong>in scope</strong> for. To add, edit, or deactivate
                a database — or to toggle which frameworks a database is in scope for — use
                that technology's <strong>DB Compliance Matrix</strong> under its
                Administration menu. Databases are auto-discovered on connection refresh.
              </div>
            </div>

            {/* Filters */}
            <div className="card-body border-bottom bg-light">
              <div className="row g-3">
                <div className="col-md-2">
                  <label className="form-label fw-bold">Technology</label>
                  <select className="form-select form-select-sm" value={filters.technology}
                          onChange={(e) => setFilter('technology', e.target.value)}>
                    <option value="">All</option>
                    {technologies.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold">Database</label>
                  <input type="text" className="form-control form-control-sm"
                         placeholder="Identifier / name / instance…"
                         value={filters.search}
                         onChange={(e) => setFilter('search', e.target.value)} />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold">Application</label>
                  <input type="text" className="form-control form-control-sm"
                         placeholder="App id or name…"
                         value={filters.app_id}
                         onChange={(e) => setFilter('app_id', e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-bold">Compliance</label>
                  <select className="form-select form-select-sm" value={filters.framework}
                          onChange={(e) => setFilter('framework', e.target.value)}>
                    <option value="">All</option>
                    {frameworks.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-bold">Environment</label>
                  <select className="form-select form-select-sm" value={filters.environment}
                          onChange={(e) => setFilter('environment', e.target.value)}>
                    <option value="">All</option>
                    {environments.map((env) => <option key={env} value={env}>{env}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-2">
                <button className="btn btn-link btn-sm p-0 text-decoration-none" onClick={clearAllFilters}>
                  <i className="fas fa-times me-1"></i>Clear filters
                </button>
              </div>
            </div>

            {/* Data Table */}
            <div className="card-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error.message || 'Failed to load the consolidated report.'}
                </div>
              )}

              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead className="table-dark">
                    <tr>
                      <th>DB Identifier</th>
                      <th>Display Name</th>
                      <th style={{ width: '110px' }}>Technology</th>
                      <th>Application</th>
                      <th style={{ width: '120px' }}>Deployment</th>
                      <th style={{ width: '120px' }}>Environment</th>
                      <th style={{ minWidth: '200px' }}>Compliance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-4">
                          <i className="fas fa-search fa-2x mb-2"></i>
                          <br />
                          No databases found{databases.length > 0 ? ' matching your filters' : ''}.
                        </td>
                      </tr>
                    ) : (
                      filtered.map((d) => (
                        <tr key={`${d.technology}-${d.database_identifier}`}>
                          <td>
                            <strong className="text-primary">{d.database_identifier}</strong>
                            {d.instance && <div className="small text-muted">{d.instance}</div>}
                          </td>
                          <td>
                            {d.display_name || d.database_name}
                            {d.database_name && d.display_name !== d.database_name && (
                              <div className="small text-muted">{d.database_name}</div>
                            )}
                          </td>
                          <td>
                            <span className={`badge bg-${DB_TYPE_COLORS[d.technology_label] || 'secondary'}`}>
                              {d.technology_label}
                            </span>
                          </td>
                          <td>
                            {/* M:N: every linked application; the star marks the primary
                                (its owner attests this database). */}
                            {(d.applications && d.applications.length
                              ? d.applications
                              : (d.app_id ? [{ app_id: d.app_id, app_name: d.app_name, is_primary: true }] : [])
                            ).map((a) => (
                              <div key={a.app_id}>
                                {a.is_primary && (
                                  <i className="fas fa-star text-warning me-1"
                                     title="Primary application — its owner attests this database"></i>
                                )}
                                <strong>{a.app_id}</strong>
                                {a.app_name && a.app_name !== a.app_id && (
                                  <span className="small text-muted"> {a.app_name}</span>
                                )}
                              </div>
                            ))}
                            {!d.app_id && (!d.applications || !d.applications.length) && '—'}
                          </td>
                          <td>
                            <span className={d.deployment_type === 'STANDALONE' ? 'text-secondary' : 'text-info'}>
                              {d.deployment_type || '—'}
                            </span>
                          </td>
                          <td>
                            <span className={envClass(d.database_environment)}>
                              {d.database_environment || 'N/A'}
                            </span>
                          </td>
                          <td>
                            {(d.in_scope_frameworks || []).length > 0 ? (
                              <div className="d-flex flex-wrap gap-2">
                                {d.in_scope_frameworks.map((fw) => (
                                  <span key={fw} className="text-success" title={`In scope for ${fw}`}>
                                    <i className="fas fa-circle fa-xs me-1"></i>{fw}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted">None</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {databases.length > 0 && (
                <div className="mt-3">
                  <small className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    Showing {filtered.length} of {databases.length} databases
                    {filtered.length !== databases.length && ' (filtered)'}
                  </small>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppDatabaseMapping;
